import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Box, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip, CircularProgress, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Stack, Grid
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getAllItineraryCards, createItineraryCard, updateItineraryCard, deleteItineraryCard } from '../../api/itineraryCardsAPI';
import useSnackbar from '../../hooks/useSnackbar';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link'],
        ['clean'],
    ],
    clipboard: { matchVisual: false },
};

const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent', 'link',
];

const ItineraryCardsPage = () => {
    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [currentCard, setCurrentCard] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        destination: '',
        description: ''
    });

    const fetchCards = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllItineraryCards();
            if (res.success) {
                setCards(res.data);
            }
        } catch (error) {
            console.error("Error fetching cards:", error);
            showSnackbar("Failed to fetch itinerary cards", "error");
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    const handleOpenDialog = (card = null) => {
        if (card) {
            setCurrentCard(card);
            setFormData({
                title: card.title,
                destination: card.destination,
                description: card.description || ''
            });
        } else {
            setCurrentCard(null);
            setFormData({ title: '', destination: '', description: '' });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentCard(null);
    };

    const handleSave = async () => {
        if (!formData.title.trim() || !formData.destination.trim()) {
            showSnackbar("Title and Destination are required", "warning");
            return;
        }

        try {
            if (currentCard) {
                await updateItineraryCard(currentCard._id, formData);
                showSnackbar("Card updated successfully", "success");
            } else {
                await createItineraryCard(formData);
                showSnackbar("Card created successfully", "success");
            }
            handleCloseDialog();
            fetchCards();
        } catch (error) {
            console.error("Error saving card:", error);
            showSnackbar(error.response?.data?.message || "Failed to save card", "error");
        }
    };

    const handleOpenDelete = (card) => {
        setCurrentCard(card);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!currentCard) return;
        try {
            await deleteItineraryCard(currentCard._id);
            showSnackbar("Card deleted successfully", "success");
            setDeleteDialogOpen(false);
            fetchCards();
        } catch (error) {
            console.error("Error deleting card:", error);
            showSnackbar("Failed to delete card", "error");
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Itinerary Cards
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                    Add New Card
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Destination</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Created At</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cards.map((card, index) => (
                                <TableRow key={card._id} sx={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <TableCell sx={{ color: '#1e293b', fontWeight: 500 }}>{card.title}</TableCell>
                                    <TableCell sx={{ color: '#475569' }}>{card.destination}</TableCell>
                                    <TableCell sx={{ color: '#475569' }}>{card.created_by?.fullName || 'Unknown'}</TableCell>
                                    <TableCell sx={{ color: '#475569', fontSize: '0.85rem' }}>
                                        {new Date(card.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(card)}
                                                sx={{ color: '#3b82f6' }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDelete(card)}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {cards.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b' }}>
                                        No itinerary cards yet. Create one to get started!
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {currentCard ? "Edit Itinerary Card" : "Create New Itinerary Card"}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            label="Title"
                            fullWidth
                            variant="outlined"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="e.g., Gangtok Sightseeing"
                        />
                        <TextField
                            label="Destination"
                            fullWidth
                            variant="outlined"
                            value={formData.destination}
                            onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                            placeholder="e.g., Gangtok"
                        />
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                Description
                            </Typography>
                            <Box sx={{ minHeight: '300px', '.ql-editor': { minHeight: '250px', maxHeight: '400px', overflowY: 'auto' } }}>
                                <ReactQuill
                                    theme="snow"
                                    value={formData.description}
                                    onChange={(content) => setFormData({ ...formData, description: content })}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter the itinerary description..."
                                />
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {currentCard ? "Update" : "Create"} Card
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete "{currentCard?.title}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            {SnackbarComponent()}
        </Container>
    );
};

export default ItineraryCardsPage;
